import { useState, useEffect, useCallback } from "react";
import { Lock, Unlock, AlertTriangle, CheckCircle, Loader2, FileText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { formatMoney } from "@/lib/money";

function StatCard({ label, value, currency = "USD", accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-1 text-xl font-bold font-mono ${accent || "text-slate-900"}`}>{formatMoney(value, currency)}</div>
    </div>
  );
}

export default function Statements() {
  const [periods, setPeriods] = useState([]);
  const [month, setMonth] = useState("");
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [fx, setFx] = useState(null);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const fetchFx = useCallback(async () => {
    try { const { data } = await adminApi.getFxRate(); setFx(data); setRateInput(data?.reference_usd_inr ?? ""); } catch {}
  }, []);

  const saveRate = async () => {
    if (!rateInput || parseFloat(rateInput) <= 0) { toast.error("Enter a positive rate"); return; }
    setSavingRate(true);
    try { await adminApi.setFxRate(parseFloat(rateInput)); toast.success("Reference rate saved"); await fetchFx(); if (statement) await loadStatement(statement.month); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSavingRate(false);
  };

  const fetchPeriods = useCallback(async () => {
    try {
      const { data } = await adminApi.getPeriods();
      setPeriods(data);
    } catch { toast.error("Failed to load periods"); }
  }, []);

  useEffect(() => { fetchPeriods(); fetchFx(); }, [fetchPeriods, fetchFx]);

  const loadStatement = async (m) => {
    if (!m || !/^\d{4}-\d{2}$/.test(m)) { toast.error("Enter month as YYYY-MM"); return; }
    setLoading(true);
    try {
      const { data } = await adminApi.getPeriodStatement(m);
      setStatement(data);
    } catch { toast.error("Failed to load statement"); }
    setLoading(false);
  };

  const doClose = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.closePeriod(month);
      toast.success(data.message + (data.unsettled_count ? ` (${data.unsettled_count} unsettled)` : ""));
      setConfirmClose(false);
      await fetchPeriods();
      await loadStatement(month);
    } catch (e) { toast.error(e.response?.data?.detail || "Close failed"); }
    setBusy(false);
  };

  const doReopen = async (m) => {
    setBusy(true);
    try {
      await adminApi.reopenPeriod(m);
      toast.success(`Reopened ${m}`);
      await fetchPeriods();
      await loadStatement(m);
    } catch (e) { toast.error(e.response?.data?.detail || "Reopen failed"); }
    setBusy(false);
  };

  const closedSet = new Set(periods.filter(p => p.status === "closed").map(p => p.month));
  const s = statement;
  const sum = s?.frozen?.summary || s?.live?.summary || {};
  const partners = (s?.is_closed ? s?.frozen?.partners : s?.live?.partners) || [];

  return (
    <div className="p-6 md:p-8" data-testid="statements-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Statements</h1>
        <p className="text-sm text-slate-500 mt-1">Close a month to freeze its revenue & payouts. Frozen figures never change; reopen to correct.</p>
      </div>

      {/* FX reference rate setter */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-4" data-testid="fx-rate-card">
        <div>
          <div className="text-sm font-semibold text-slate-700">Reference USD → INR rate</div>
          <p className="text-xs text-slate-500 mt-0.5 max-w-md">Used to express INR payouts in USD on admin totals and for indicative INR views. Confirmed per-invoice rates (set at settlement) always take precedence.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">₹ per $1</label>
            <Input type="number" step="0.0001" value={rateInput} onChange={e => setRateInput(e.target.value)} placeholder="83.5" className="h-10 w-32 border-slate-200" data-testid="fx-rate-input" />
          </div>
          <Button onClick={saveRate} disabled={savingRate} className="h-10 bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-fx-rate-btn">
            {savingRate ? <Loader2 size={16} className="animate-spin" /> : "Save Rate"}
          </Button>
        </div>
        {fx?.reference_usd_inr && (
          <span className="text-xs text-slate-400 ml-auto self-center">Current: ₹{fx.reference_usd_inr}/$ {fx.updated_at ? `· updated ${fx.updated_at.slice(0, 10)}` : ""}</span>
        )}
      </div>

      {/* Month picker */}
      <div className="flex items-end gap-3 mb-6">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-500">Month (YYYY-MM)</label>
          <Input value={month} onChange={e => setMonth(e.target.value)} placeholder="2026-06" className="h-10 w-40 border-slate-200" data-testid="statement-month-input" />
        </div>
        <Button onClick={() => loadStatement(month)} disabled={loading} className="h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="load-statement-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <><FileText size={16} className="mr-1.5" /> View Statement</>}
        </Button>
      </div>

      {/* Closed periods list */}
      {periods.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="closed-periods-list">
          {periods.map(p => (
            <button key={p.month} onClick={() => { setMonth(p.month); loadStatement(p.month); }}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${p.status === "closed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
              data-testid={`period-chip-${p.month}`}>
              {p.status === "closed" ? <Lock size={12} /> : <Unlock size={12} />} {p.month} · {p.status}
            </button>
          ))}
        </div>
      )}

      {s && (
        <div className="space-y-6" data-testid="statement-content">
          {/* Header + actions */}
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full ${s.is_closed ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="statement-status">
                {s.is_closed ? <><Lock size={14} /> Closed</> : <><Unlock size={14} /> Open (live)</>}
              </span>
              <span className="text-lg font-bold text-slate-900">{s.month}</span>
              {s.drift?.diverged && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600" data-testid="statement-drift">
                  <AlertTriangle size={13} /> Data changed since close · Δ {s.drift.delta > 0 ? "+" : ""}{formatMoney(s.drift.delta, "USD")}
                </span>
              )}
              {s.is_closed && s.drift && !s.drift.diverged && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle size={13} /> In sync</span>
              )}
            </div>
            <div className="flex gap-2">
              {s.is_closed ? (
                <Button onClick={() => doReopen(s.month)} disabled={busy} variant="outline" className="h-9 text-sm border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="reopen-period-btn">
                  <Unlock size={14} className="mr-1.5" /> Reopen
                </Button>
              ) : (
                <Button onClick={() => setConfirmClose(true)} disabled={busy} className="h-9 text-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="close-period-btn">
                  <Lock size={14} className="mr-1.5" /> Close Month
                </Button>
              )}
            </div>
          </div>

          {/* Drift comparison (closed + diverged) */}
          {s.is_closed && s.drift?.diverged && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="drift-panel">
              <div className="flex items-center gap-6 text-sm">
                <div><span className="text-amber-600">Frozen (statement)</span><div className="font-mono font-bold text-slate-900">{formatMoney(s.drift.frozen_net_revenue, "USD")}</div></div>
                <RefreshCw size={16} className="text-amber-400" />
                <div><span className="text-amber-600">Live now</span><div className="font-mono font-bold text-slate-900">{formatMoney(s.drift.live_net_revenue, "USD")}</div></div>
                <div><span className="text-amber-600">Deviation</span><div className="font-mono font-bold text-amber-700">{s.drift.delta > 0 ? "+" : ""}{formatMoney(s.drift.delta, "USD")}</div></div>
              </div>
              <p className="text-xs text-amber-600 mt-2">The statement number is your source of truth. Reopen → correct → re-close if this change is legitimate.</p>
            </div>
          )}

          {/* Waterfall summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Gross Revenue" value={sum.total_earnings} />
            <StatCard label="Ad Server Cost" value={sum.ad_server_cost} accent="text-red-600" />
            <StatCard label="SLCP" value={sum.content_partner_payouts} accent="text-fuchsia-600" />
            <StatCard label="Net Revenue" value={sum.net_revenue} accent="text-emerald-700" />
            <StatCard label="SLDP Payouts" value={sum.partner_earnings} accent="text-blue-700" />
            <StatCard label="Admin Retained" value={sum.admin_earnings} accent="text-slate-900" />
          </div>

          {/* Per-partner payout lines */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Partner Payouts</div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4">Currency</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4 text-right">USD Base</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4 text-right">FX Rate</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4 text-right">Billed</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-2.5 px-4">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No partner invoices for this month</TableCell></TableRow>
                ) : partners.map((p) => (
                  <TableRow key={p.partner_id} className="border-b border-slate-100" data-testid={`payout-row-${p.partner_id}`}>
                    <TableCell className="py-2.5 px-4 text-sm text-slate-800">{p.partner_name}</TableCell>
                    <TableCell className="py-2.5 px-4 text-sm">{p.currency === "INR" ? "₹ INR" : "$ USD"}</TableCell>
                    <TableCell className="py-2.5 px-4 text-sm text-right font-mono text-slate-600">{p.usd_amount != null ? formatMoney(p.usd_amount, "USD") : "—"}</TableCell>
                    <TableCell className="py-2.5 px-4 text-sm text-right font-mono text-slate-600">{p.fx_rate ?? "—"}</TableCell>
                    <TableCell className="py-2.5 px-4 text-sm text-right font-mono font-semibold text-slate-900">{p.amount_billed != null ? formatMoney(p.amount_billed, p.currency) : <span className="text-amber-600 text-xs">pending</span>}</TableCell>
                    <TableCell className="py-2.5 px-4">
                      {p.settled
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle size={12} /> Settled</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle size={12} /> Not settled</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Confirm close dialog */}
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-lg">Close {month}?</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2 text-sm text-slate-600">
            <p>This freezes {month}'s revenue and payouts into a permanent statement. Dashboards keep showing live data with a drift flag, but the statement stays fixed.</p>
            {partners.some(p => !p.settled) && (
              <p className="text-amber-600 bg-amber-50 border border-amber-100 rounded-md p-2.5 text-xs">
                <AlertTriangle size={13} className="inline mr-1" /> Some invoices aren't settled yet — those partners are captured in USD and flagged. You can reopen and re-close after settling them.
              </p>
            )}
            <Button onClick={doClose} disabled={busy} className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-close-btn">
              {busy ? "Closing..." : "Close Month"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
